import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { MobileShortcuts } from '@/components/layout/MobileShortcuts.js';
import { HeroBanner } from '@/features/banners/HeroBanner.js';
import { useBrandingStore } from '@/stores/branding.js';
import type { ReactNode } from 'react';

interface HeroProps {
  children?: ReactNode; // HeroCarousel slot
}

/**
 * Immersive hero scene — ported 1:1 from the approved mockup
 * (.superpowers/brainstorm/.../12-hero-wheel-arc.html `.scene` + `.center`).
 * The (transparent) TopBar floats over the top of this scene; `-mt-16` pulls
 * the scene up behind it, `-mx-*` makes it full-bleed across the main column.
 */
export function Hero({ children }: HeroProps) {
  const { t } = useTranslation('common');
  const sitename = useBrandingStore((s) => s.branding?.sitename) ?? '';
  return (
    <div className="bf-night relative -mx-4 md:-mx-6 min-h-[620px] flex flex-col">
      {/* The Bifröst illustration is the page-wide hero background (AppShell
          .bf-hero-bg), so the hero itself stays transparent. In the light theme
          the stage and curtain keep it a night scene and hand over to the light
          page below (globals.css); both render nothing in the dark theme. */}
      <div aria-hidden className="bf-hero-stage" />
      <div aria-hidden className="bf-hero-curtain" />

      {/* Readability scrim — FULL-BLEED. .bf-hero-bg spans the whole viewport, so a
          scrim limited to the 1280px content column reads as a hard-edged dark box
          past its edges on ultra-wide screens. w-screen + centered, and kept OUTSIDE
          the overflow-hidden clip below so it isn't trimmed back to the column. */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-screen z-0 h-[62%] bg-gradient-to-b from-black/55 via-black/35 to-transparent"
      />

      {/* overflow-hidden clips the fanned card carousel to the column. */}
      <div className="relative z-[1] flex flex-1 flex-col overflow-hidden">
        {/* .center — content sits over the illustration */}
        <div className="flex flex-1 flex-col px-5 pt-20 md:pt-24 pb-10 text-center">
        <h1 className="sr-only">{sitename || t('nav.home')}</h1>
        {/* The first of these sits under the transparent TopBar on phone and
            tablet (the -mt pull-up); from lg the shortcut grid is hidden and the
            banner starts below the bar. With no banner the grid moves up. */}
        <div className="-mt-16 md:-mt-20 lg:mt-0 flex flex-col gap-6">
          <HeroBanner />
          <MobileShortcuts />
        </div>

        {/* Section title — Originals (fancy display font) */}
        <div className="bf-display mt-12 text-[28px] md:text-[40px] leading-none text-white drop-shadow-[0_2px_10px_var(--primary-glow)]">
          {sitename ? `${sitename} ` : ''}<span className="text-[var(--accent-strong)]">Originals</span>
        </div>

        {children}

        {/* View-all → the dedicated Originals grid (same target as the nav tab). */}
        <div className="mt-5 flex justify-center">
          <Link
            to="/games/originals"
            className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
          >
            {t('common.viewAll', { defaultValue: 'ดูทั้งหมด' })}
            <span aria-hidden>→</span>
          </Link>
        </div>

        </div>
      </div>
    </div>
  );
}
